import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { TWclassNames } from "../../utils/Div";

const ACTIVE_BG = "#EAF3FD";

/**
 * Left sidebar: DOKUMENTSTRUKTUR — ordered selectable blocks + dashed add.
 * Claude-aligned: 230px flush panel, uppercase muted header, selected state.
 */
export default function OfferDocumentStructure({
  blocks = [],
  selectedBlockId,
  onSelect,
  onAddBlock,
  onMoveUp,
  onMoveDown,
}) {
  const { t } = useTranslation();

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-white">
      <div className="px-3 pb-2 pt-0.5">
        <p className="text-[11px] font-[650] uppercase tracking-[0.04em] text-slate-400">
          {t("offers.documentBuilder.structure_title")}
        </p>
      </div>

      <ul className="flex-1 space-y-0.5 overflow-y-auto px-1.5 pb-1" role="listbox">
        {blocks.map((block, index) => {
          const selected = block.id === selectedBlockId;
          const disabled = block.enabled === false;
          return (
            <li key={block.id} className="group flex items-stretch gap-0.5">
              <button
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => onSelect?.(block.id)}
                className={TWclassNames(
                  "min-w-0 flex-1 rounded-md px-2 py-[7px] text-left text-xs transition-colors",
                  selected
                    ? "font-semibold text-[#0B3A78]"
                    : disabled
                    ? "font-medium text-slate-400 hover:bg-slate-50"
                    : "font-medium text-slate-700 hover:bg-slate-50"
                )}
                style={
                  selected
                    ? { backgroundColor: ACTIVE_BG }
                    : undefined
                }
              >
                <span className="flex items-center gap-1.5 truncate">
                  {disabled ? (
                    <span
                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300"
                      title={t("offers.documentBuilder.editor.enabled_hint")}
                      aria-hidden="true"
                    />
                  ) : null}
                  <span className="truncate">{block.title}</span>
                </span>
              </button>
              <div
                className={TWclassNames(
                  "flex flex-col justify-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100",
                  selected && "opacity-100"
                )}
              >
                <button
                  type="button"
                  disabled={index === 0}
                  onClick={() => onMoveUp?.(block.id)}
                  className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                  title={t("offers.documentBuilder.move_up")}
                  aria-label={t("offers.documentBuilder.move_up")}
                >
                  <ChevronUpIcon className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  disabled={index === blocks.length - 1}
                  onClick={() => onMoveDown?.(block.id)}
                  className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                  title={t("offers.documentBuilder.move_down")}
                  aria-label={t("offers.documentBuilder.move_down")}
                >
                  <ChevronDownIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="px-1.5 pb-1 pt-2">
        <button
          type="button"
          onClick={() => onAddBlock?.()}
          className="flex w-full items-center justify-center rounded-[7px] border border-dashed border-slate-300 bg-slate-50 px-3 py-[7px] text-xs font-medium text-slate-500 transition-colors hover:border-slate-400 hover:bg-slate-100 hover:text-slate-700"
        >
          {t("offers.documentBuilder.add_block")}
        </button>
      </div>
    </div>
  );
}
